import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GmailOAuth } from '@/components/email/gmail-oauth';
import { emailAccountsAPI } from '@/lib/api';
import { useTeams } from '@/hooks/use-teams';
import { useToast } from '@/hooks/use-toast';
import { EmailAccount } from '@/types/email-accounts';
import { 
  Mail, 
  Settings as SettingsIcon, 
  Shield, 
  Clock,
  AlertCircle,
  CheckCircle,
  Zap
} from 'lucide-react';

export function EmailSettingsPage() {
  const [emailSettings, setEmailSettings] = useState({
    signature: '',
    replyTo: '',
    bcc: '',
    dailyLimit: 50,
    sendDelay: 30,
    businessHoursOnly: true,
    startTime: '09:00',
    endTime: '17:00',
    skipWeekends: true,
  });

  const { toast } = useToast();
  const { currentTeam } = useTeams();

  const { data: accountsResponse, isLoading } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => emailAccountsAPI.getAccounts(),
  });

  const { data: statusResponse } = useQuery({
    queryKey: ['email-status'],
    queryFn: () => emailAccountsAPI.getStatus(),
  });

  const accounts = accountsResponse?.data as EmailAccount[] | undefined || [];
  const status = statusResponse?.data;
  const gmailAccount = accounts.find(account => account.provider === 'gmail');
  const isGmailConnected = !!gmailAccount && gmailAccount.isActive;

  const handleSaveSettings = () => {
    // TODO: Implement save settings API call
    toast({
      title: 'Settings Saved',
      description: 'Your email settings have been updated.',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Email Settings</h1>
          <p className="text-muted-foreground">
            Configure your email accounts and sending preferences
          </p>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Mail className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Email Account
                </p>
                <p className="text-2xl font-bold flex items-center">
                  {isGmailConnected ? (
                    <>
                      <CheckCircle className="h-5 w-5 text-green-600 mr-1" />
                      Connected
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-5 w-5 text-red-600 mr-1" />
                      Not Connected
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Zap className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Daily Limit
                </p>
                <p className="text-2xl font-bold">
                  {emailSettings.dailyLimit}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Send Delay
                </p>
                <p className="text-2xl font-bold">
                  {emailSettings.sendDelay}s
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Status
                </p>
                <p className="text-2xl font-bold">
                  {(status as any)?.message || 'Ready'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Settings */}
      <Tabs defaultValue="accounts" className="space-y-6">
        <TabsList>
          <TabsTrigger value="accounts">Email Accounts</TabsTrigger>
          <TabsTrigger value="sending">Sending Settings</TabsTrigger>
          <TabsTrigger value="templates">Default Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts" className="space-y-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-2">Connected Accounts</h3>
              <p className="text-muted-foreground mb-4">
                Connect and manage your email accounts for sending campaigns.
              </p>
            </div>

            <GmailOAuth 
              onConnectionChange={(isConnected) => {
                if (isConnected) {
                  toast({
                    title: 'Account Connected',
                    description: 'Your Gmail account is now ready to send campaigns.',
                  });
                }
              }}
            />

            {!isGmailConnected && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You need to connect at least one email account to send campaigns. 
                  Gmail is currently the only supported provider.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>

        <TabsContent value="sending" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Sending Preferences</CardTitle>
              <CardDescription>
                Configure how and when your emails are sent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="dailyLimit">Daily Email Limit</Label>
                  <Input
                    id="dailyLimit"
                    type="number"
                    value={emailSettings.dailyLimit}
                    onChange={(e) =>
                      setEmailSettings(prev => ({
                        ...prev,
                        dailyLimit: parseInt(e.target.value) || 50,
                      }))
                    }
                    min="1"
                    max="500"
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum emails to send per day (recommended: 50-100)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sendDelay">Send Delay (seconds)</Label>
                  <Input
                    id="sendDelay"
                    type="number"
                    value={emailSettings.sendDelay}
                    onChange={(e) =>
                      setEmailSettings(prev => ({
                        ...prev,
                        sendDelay: parseInt(e.target.value) || 30,
                      }))
                    }
                    min="10"
                    max="300"
                  />
                  <p className="text-xs text-muted-foreground">
                    Delay between emails to avoid spam detection
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Business Hours Only</Label>
                    <p className="text-sm text-muted-foreground">
                      Only send emails during business hours
                    </p>
                  </div>
                  <Switch
                    checked={emailSettings.businessHoursOnly}
                    onCheckedChange={(checked) =>
                      setEmailSettings(prev => ({
                        ...prev,
                        businessHoursOnly: checked,
                      }))
                    }
                  />
                </div>

                {emailSettings.businessHoursOnly && (
                  <div className="grid grid-cols-2 gap-4 ml-4">
                    <div className="space-y-2">
                      <Label htmlFor="startTime">Start Time</Label>
                      <Input
                        id="startTime"
                        type="time"
                        value={emailSettings.startTime}
                        onChange={(e) =>
                          setEmailSettings(prev => ({
                            ...prev,
                            startTime: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endTime">End Time</Label>
                      <Input
                        id="endTime"
                        type="time"
                        value={emailSettings.endTime}
                        onChange={(e) =>
                          setEmailSettings(prev => ({
                            ...prev,
                            endTime: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Skip Weekends</Label>
                    <p className="text-sm text-muted-foreground">
                      Don't send emails on Saturday and Sunday
                    </p>
                  </div>
                  <Switch
                    checked={emailSettings.skipWeekends}
                    onCheckedChange={(checked) =>
                      setEmailSettings(prev => ({
                        ...prev,
                        skipWeekends: checked,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveSettings}>
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Email Signature & Headers</CardTitle>
              <CardDescription>
                Configure default email signature and headers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signature">Email Signature</Label>
                <Textarea
                  id="signature"
                  value={emailSettings.signature}
                  onChange={(e) =>
                    setEmailSettings(prev => ({
                      ...prev,
                      signature: e.target.value,
                    }))
                  }
                  placeholder="Best regards,&#10;Your Name&#10;Your Company"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="replyTo">Reply-To Address</Label>
                  <Input
                    id="replyTo"
                    type="email"
                    value={emailSettings.replyTo}
                    onChange={(e) =>
                      setEmailSettings(prev => ({
                        ...prev,
                        replyTo: e.target.value,
                      }))
                    }
                    placeholder="replies@yourcompany.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bcc">BCC Address</Label>
                  <Input
                    id="bcc"
                    type="email"
                    value={emailSettings.bcc}
                    onChange={(e) =>
                      setEmailSettings(prev => ({
                        ...prev,
                        bcc: e.target.value,
                      }))
                    }
                    placeholder="archive@yourcompany.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Default Email Templates</CardTitle>
              <CardDescription>
                Set default templates for different types of campaigns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Template management is available on the Email Templates page. 
                  Visit that page to create and manage your templates.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}